import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Loader2, TrendingUp, DollarSign, Percent, Hash } from 'lucide-react';
import { PortfolioFactorCard, RateDriftCard, BenchmarkAlertsCard } from './MarginFactorDriftCards';
import { PortfolioFactorHistoryCard } from './PortfolioFactorHistoryCard';
import { JustificationInsightsCard } from './JustificationInsightsCard';
import { RolePricingCard } from './RolePricingCard';
import { RepMarginLeaderboardCard } from './RepMarginLeaderboardCard';
import { RateHistoryTimelineCard } from './RateHistoryTimelineCard';
import { DealSummaryDialog } from './DealSummaryDialog';
import { FxMovementCard } from './FxMovementCard';
import { MARGIN_VIEWS, MarginViewSwitcher, viewKey } from './MarginViewSwitcher';
import { MarginByEstimateTable, MarginRegionCard } from './MarginByEstimateTable';
import { CurrencyModeToggle, useCurrencyMode } from './CurrencyMode';
import { ProductMarginBreakdownCards } from './ProductMarginBreakdownCards';
import { MarginBreakEvenCard } from './MarginBreakEvenCard';
import { TermOverrideReportCard } from './TermOverrideReportCard';
import { useAuth } from '../../context/AuthContext';
import { MarginFilterBar, EMPTY_FILTERS, normalizeFilters, filterParams, activeFilterCount } from './MarginFilterBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtA = (v) => `A$${Math.round(v || 0).toLocaleString()}`;

const Kpi = ({ icon: Icon, label, value, sub, testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-xl font-bold text-[#0F2040] mt-1">{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

const COST_SUB = { combined: 'internal cost rates + price-book cost basis', implementation: 'internal cost rates', product: 'price-book cost basis' };

/** Leadership-only portfolio margin view — Combined (default) | Implementation | Product, sell vs internal cost in AUD. */
export default function MarginInsightsTab() {
  const { user } = useAuth();
  const [dealDrill, setDealDrill] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [viewId, setViewId] = useState('combined');
  const [ccyMode, setCcyMode] = useCurrencyMode();
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [views, setViews] = useState([]);
  const params = useMemo(() => filterParams(filters), [filters]);
  const filterKey = JSON.stringify(params);
  const prefsLoaded = useRef(false);
  const [prefsReady, setPrefsReady] = useState(false);
  const view = MARGIN_VIEWS.find((v) => v.id === viewId) || MARGIN_VIEWS[0];
  useEffect(() => {
    axios.get(`${API_URL}/api/auth/preferences`, { withCredentials: true })
      .then((r) => {
        const p = r.data?.preferences || {};
        if (p.margin_filters) setFilters(normalizeFilters(p.margin_filters));
        if (Array.isArray(p.margin_filter_views)) setViews(p.margin_filter_views);
        if (MARGIN_VIEWS.some((v) => v.id === p.margin_view)) setViewId(p.margin_view);
      })
      .catch(() => {}).finally(() => { prefsLoaded.current = true; setPrefsReady(true); });
  }, []);
  useEffect(() => {
    if (!prefsLoaded.current) return;
    const t = setTimeout(() => {
      axios.put(`${API_URL}/api/auth/preferences`, { margin_filters: filters, margin_view: viewId, margin_filter_views: views }, { withCredentials: true }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [filters, viewId, views]);
  const viewRows = useMemo(() => (data?.rows || []).filter((r) => (viewId !== 'product' || r.has_product)), [data, viewId]);

  const reqSeq = useRef(0);
  useEffect(() => {
    if (!prefsReady) return;
    const seq = ++reqSeq.current;
    axios.get(`${API_URL}/api/insights/margins`, { params, withCredentials: true })
      .then(res => { if (seq === reqSeq.current) setData(res.data); })
      .catch(e => setError(e.response?.data?.detail || 'Failed to load margins'));
  }, [filterKey, prefsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <p className="text-sm text-rose-600 py-8" data-testid="margin-tab-error">{error}</p>;
  if (!data) return <div className="flex items-center gap-2 text-sm text-slate-400 py-10"><Loader2 className="w-4 h-4 animate-spin" /> Loading margins…</div>;
  if (!data.configured) {
    return (
      <Card className="border-amber-200 bg-amber-50/40" data-testid="margin-not-configured">
        <CardContent className="p-6 text-sm text-amber-800">
          No internal cost rates are set yet. Add them in <b>Admin → Settings → Internal Cost Rates (A$/hr)</b> to unlock margin tracking.
        </CardContent>
      </Card>
    );
  }

  const t = data.totals || {};
  const k = (f) => t[viewKey(view, f)];
  const count = viewId === 'product' ? t.product_deals : t.count;
  const below = data[view.below] || 0;
  const isImpl = viewId === 'implementation';
  const canSend = ['admin', 'cro', 'cco', 'super_admin'].includes(user?.role);
  return (
    <div className="space-y-5" data-testid="margin-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <MarginViewSwitcher view={viewId} onChange={setViewId} counts={{ combined: t.count, implementation: t.count, product: t.product_deals }} />
        <div className="flex items-center gap-2">
          <CurrencyModeToggle mode={ccyMode} onChange={setCcyMode} testId="margin-ccy-mode" />
          {ccyMode === 'native' && <span className="text-[10px] text-slate-400" data-testid="margin-ccy-note">Deal rows in their own currency · KPIs, regions and leaderboards stay in A$</span>}
        </div>
      </div>
      <MarginFilterBar facets={data.facets} filters={filters} setFilters={setFilters} matched={t.count} universe={data.universe_count} views={views} setViews={setViews} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid={`margin-kpis-${viewId}`}>
        <Kpi icon={DollarSign} label={`${view.label} Sell (A$)`} value={fmtA(k('sell_aud'))}
          sub={viewId === 'product' ? `${count} deal${count === 1 ? '' : 's'} · full term, net of discounts` : viewId === 'combined' ? `${t.count} estimates · ${t.product_deals} with subscription · TCV` : `${t.count} estimates · converted`} testId="margin-kpi-sell" />
        <Kpi icon={Hash} label={`${view.label} Cost (A$)`} value={fmtA(k('cost_aud'))} sub={COST_SUB[viewId]} testId="margin-kpi-cost" />
        <Kpi icon={TrendingUp} label={`${view.label} Margin (A$)`} value={fmtA(k('margin_aud'))} testId="margin-kpi-margin" />
        <Kpi icon={Percent} label={`${view.label} Portfolio Margin`} value={k('margin_pct') != null ? `${k('margin_pct')}%` : '—'}
          sub={data.min_margin_pct != null ? `${below} below ${data.min_margin_pct}% guardrail` : 'no guardrail set'} testId="margin-kpi-pct" />
      </div>
      {viewId === 'combined' && t.product_deals > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="margin-split-kpis">
          <Kpi icon={DollarSign} label="Implementation Sell (A$)" value={fmtA(t.sell_aud)} sub={`${t.margin_pct != null ? `${t.margin_pct}%` : '—'} margin`} testId="margin-kpi-impl-sell" />
          <Kpi icon={DollarSign} label="Product Subscriptions (A$)" value={fmtA(t.product_sell_aud)} sub={`${t.product_margin_pct != null ? `${t.product_margin_pct}%` : '—'} margin · ${t.product_deals} deal${t.product_deals === 1 ? '' : 's'}`} testId="margin-kpi-product-sell" />
          <Kpi icon={Hash} label="Implementation Cost (A$)" value={fmtA(t.cost_aud)} sub="internal cost rates" testId="margin-kpi-impl-cost" />
          <Kpi icon={Hash} label="Product Cost (A$)" value={fmtA(t.product_cost_aud)} sub="price-book cost basis" testId="margin-kpi-product-cost" />
        </div>
      )}
      {viewId === 'combined' && <MarginBreakEvenCard onOpenDeal={setDealDrill} filterParams={params} filterKey={filterKey} />}
      {viewId === 'product' && <ProductMarginBreakdownCards byPackage={data.by_package} byComponent={data.by_component} minPct={data.min_margin_pct} />}
      {viewId === 'product' && <TermOverrideReportCard onOpenDeal={setDealDrill} filterParams={params} filterKey={filterKey} />}
      {isImpl && (
        <div className="grid lg:grid-cols-2 gap-4">
          <PortfolioFactorCard filterParams={params} filters={filters} />
          <RateDriftCard canSend={canSend} />
        </div>
      )}
      {isImpl && <BenchmarkAlertsCard onOpenDeal={setDealDrill} />}
      {isImpl && <PortfolioFactorHistoryCard />}
      {isImpl && <RolePricingCard />}
      {isImpl && <RateHistoryTimelineCard />}
      {viewId !== 'product' && <RepMarginLeaderboardCard onOpenDeal={setDealDrill} filterParams={params} filterKey={filterKey}
        onPickRep={(name) => { setFilters((f) => ({ ...f, rep: f.rep.includes(name) ? f.rep.filter((x) => x !== name) : [name] })); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />}
      {isImpl && <JustificationInsightsCard onOpenDeal={setDealDrill} />}
      <DealSummaryDialog estimateId={dealDrill} open={!!dealDrill} onClose={() => setDealDrill(null)} />
      {isImpl && <FxMovementCard />}
      <MarginRegionCard view={view} regions={data.by_region} />
      <MarginByEstimateTable view={view} rows={viewRows} minPct={data.min_margin_pct} onOpenDeal={setDealDrill} ccyMode={ccyMode}
        filtersSlot={activeFilterCount(filters) > 0 ? <span className="text-[11px] text-slate-500" data-testid="margin-table-filter-note">{t.count} of {data.universe_count} estimates · page filters applied</span> : null} />
    </div>
  );
}
